import { useEffect, useState, useRef } from 'react';
import styles from './SearchBox.module.scss';
import { Mixpanel } from '../../../../../../../utils/mixpanel';
import useDebounce from '../../../../../../../utils/constants/useDebounce';
import clsx from 'clsx';
import { searchCutIcon } from '../../../../../../../assets/hostedassets';
import { useAppSelector } from '../../../../../../../app/hooks';
import SearchIcon from '../../../../../../../assets/Svg/SearchIcon';
import { useSearchParams } from 'react-router-dom';

interface DropdownOption {
    id: string;
    label: string;
}

interface SearchBoxProps {
    searchTerm?: string;
    setSearchTerm?: (v: string) => void;
    onSearch?: (companyName: string) => void;
    categoryParam: string;
    location: any;
    accessTierParam: string;
    placeholder?: string;
    showDropdown?: boolean;
    dropdownOptions?: DropdownOption[];
    onOptionSelect?: (id: string) => void;
    allowResize?: boolean;
}

const SearchBox: React.FC<SearchBoxProps> = ({
    searchTerm,
    setSearchTerm,
    onSearch,
    categoryParam,
    location,
    accessTierParam,
    showDropdown = false,
    dropdownOptions = [],
    onOptionSelect,
    placeholder = 'Search by Company/Lender Name (Min 3 char)',
    allowResize = false,
}) => {
    const [queryText, setQueryText] = useState('');
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const debouncedQueryText = useDebounce(queryText.trim(), 300);
    const searchBoxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [queryParams, setQueryParams] = useSearchParams();

    const selectedCategory = queryParams.get('category');

    const commentThreadState = useAppSelector((state) => state.commentsThreadReducer);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
                setIsDropdownVisible(false);
                if (queryText.length === 0) {
                    setIsExpanded(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [queryText]);

    useEffect(() => {
        if (debouncedQueryText.length >= 3) {
            handleSearch(debouncedQueryText);
            setHasSearched(true);
        } else if (debouncedQueryText.length === 0 && hasSearched) {
            handleSearch('');
            setHasSearched(false);
        }
    }, [debouncedQueryText]);

    useEffect(() => {
        setQueryText('');
        setHasSearched(false);
    }, [categoryParam, accessTierParam]);

    useEffect(() => {
        setQueryText('');
        setIsExpanded(false);
        setHasSearched(false);
    }, [selectedCategory]);

    const handleSearch = (query: string) => {
        setSearchTerm?.(query);
        onSearch?.(query);
        if (query.length >= 3) {
            const trackProperties = {
                search_query: query,
                search_length: query.length,
                view: categoryParam,
                timestamp: new Date().toISOString(),
            };
            Mixpanel.track('Search Button Click', trackProperties);
        }
    };

    const handleTextSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setQueryText(value);
        setIsDropdownVisible(value.length >= 3 && showDropdown);
    };

    const handleOptionSelection = (id: string) => {
        setIsDropdownVisible(false);
        const selected = dropdownOptions.find((option) => option.id === id);
        if (selected) {
            setQueryText(selected.label);
            onOptionSelect?.(id);
            handleSearch(selected.label);
            setHasSearched(true);
        }
    };

    const handleExpandSearch = () => {
        setIsExpanded(true);
        setTimeout(() => {
            inputRef.current?.focus();
        }, 300);
    };

    const handleCollapseSearch = () => {
        setIsExpanded(false);
        // Only trigger search reset if a search was previously executed
        if (hasSearched) {
            setQueryText('');
            handleSearch('');
            setHasSearched(false);
        } else {
            setQueryText('');
        }
    };

    return (
        <div
            className={clsx(styles.searchBox, {
                [styles.searchBoxCommentsActive]:
                    commentThreadState.showCommentsThread && !commentThreadState.closingCommentsThread && allowResize,
                [styles.expanded]: isExpanded,
            })}
            ref={searchBoxRef}
        >
            <div
                className={clsx(styles.boxWrapper, {
                    [styles.expanded]: isExpanded,
                })}
                onClick={!isExpanded ? handleExpandSearch : undefined}
            >
                <input
                    ref={inputRef}
                    className={clsx(styles.searchInput, {
                        [styles.HasData]: !!queryText,
                        [styles.collapsed]: !isExpanded,
                    })}
                    type="text"
                    onChange={handleTextSearch}
                    value={queryText}
                    placeholder={isExpanded ? placeholder : ''}
                    disabled={!isExpanded}
                />
                {isExpanded ? (
                    <img alt="Cancel Icon" src={searchCutIcon} className={styles.cancelIcon} onClick={handleCollapseSearch} />
                ) : (
                    <SearchIcon
                        className={clsx(styles.searchIcon, {
                            [styles.iconBadge]: !isExpanded,
                        })}
                        onClick={!isExpanded ? handleExpandSearch : undefined}
                    />
                )}
            </div>

            {showDropdown && isDropdownVisible && isExpanded && (
                <div className={styles.dropdownContainer}>
                    {dropdownOptions.length > 0 ? (
                        dropdownOptions.map((option) => (
                            <div key={option.id} className={styles.dropdownItem} onClick={() => handleOptionSelection(option.id)}>
                                {option.label}
                            </div>
                        ))
                    ) : (
                        <div className={styles.dropdownItem}>No company available</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchBox;
